import { createSeedState, COMPANIES, DEMO_DAY, CENTROS, TITULACION_LIST, DEVICES, shortTit } from "./seed";

const SEED_STATE = createSeedState();

export function isInside(state, participantId) {
  const evs = state.accessEvents.filter((e) => e.participantId === participantId && e.day === DEMO_DAY).sort((a, b) => a.ts - b.ts);
  return evs.length > 0 && evs[evs.length - 1].type === "entry";
}

const inc = (map, k, n = 1) => { if (k) map[k] = (map[k] || 0) + n; };

function computeLive(state) {
  const pMap = Object.fromEntries(state.participants.map((p) => [p.id, p]));
  const events = state.accessEvents.filter((e) => e.day === DEMO_DAY).sort((a, b) => a.ts - b.ts);
  const seen = new Set();
  const last = {};
  let entries = 0, exits = 0, reentries = 0;
  events.forEach((e) => {
    if (e.type === "entry") {
      entries++;
      if (seen.has(e.participantId)) reentries++;
      seen.add(e.participantId);
    } else exits++;
    last[e.participantId] = e.type;
  });
  const today = state.interactions.filter((i) => i.day === DEMO_DAY);
  const byCompany = {}, byTitulacion = {}, byCentro = {};
  today.forEach((i) => { inc(byCompany, i.companyId); inc(byTitulacion, pMap[i.participantId]?.titulacion); });
  state.participants.forEach((p) => inc(byCentro, CENTROS.includes(p.centro) ? p.centro : "Otros"));
  return {
    entries, exits, reentries,
    inside: Object.values(last).filter((t) => t === "entry").length,
    unique: seen.size,
    cv: today.length,
    cvParticipants: new Set(today.map((i) => i.participantId)).size,
    registered: state.participants.length,
    byCompany, byTitulacion, byCentro,
  };
}

const SEED_LIVE = computeLive(SEED_STATE);
const diffMap = (a, b) => Object.fromEntries([...new Set([...Object.keys(a), ...Object.keys(b)])].map((k) => [k, (a[k] || 0) - (b[k] || 0)]));

function liveDelta(state) {
  const l = computeLive(state);
  const d = {};
  ["entries", "exits", "reentries", "inside", "unique", "cv", "cvParticipants", "registered"].forEach((k) => { d[k] = l[k] - SEED_LIVE[k]; });
  d.byCompany = diffMap(l.byCompany, SEED_LIVE.byCompany);
  d.byTitulacion = diffMap(l.byTitulacion, SEED_LIVE.byTitulacion);
  d.byCentro = diffMap(l.byCentro, SEED_LIVE.byCentro);
  return d;
}

const DAY2 = {
  unique: 7415, inside: 4217, cv: 18742, entries: 6833, exits: 2616, reentries: 734, cvParticipants: 6911, companiesVisited: 2.7,
  peak: { value: 5106, time: "11:34" },
  ranking: { bosch: 533, siemens: 487, "power-electronics": 442, deloitte: 419, ford: 397, capgemini: 368, accenture: 341, "mercadona-tech": 322 },
  titulaciones: { "Ingeniería Informática": 4211, "Ingeniería Industrial": 3786, Telecomunicaciones: 2341, "Administración y Dirección de Empresas": 1954, "Ciencia de Datos": 1623, "Ingeniería Eléctrica": 1388, "Ingeniería Civil": 1102, "Ingeniería Química": 936 },
};
const DAY1 = {
  unique: 6981, inside: 0, cv: 15326, entries: 7402, exits: 7402, reentries: 812, cvParticipants: 5874, companiesVisited: 2.5,
  peak: { value: 5482, time: "12:08" },
  ranking: { bosch: 498, siemens: 455, deloitte: 431, "power-electronics": 402, ford: 376, accenture: 359, capgemini: 344, "mercadona-tech": 301 },
  titulaciones: { "Ingeniería Informática": 4012, "Ingeniería Industrial": 3655, Telecomunicaciones: 2210, "Administración y Dirección de Empresas": 1702, "Ciencia de Datos": 1489, "Ingeniería Eléctrica": 1201, "Ingeniería Civil": 1013, "Ingeniería Química": 844 },
};
const CENTROS_BASE = { ETSII: 2814, ETSIINF: 2475, ETSIT: 1652, FADE: 1318, ETSICCP: 984, Otros: 1598 };
const REGISTERED_BASE = 10842;

export const OCC_TIMES = Array.from({ length: 23 }, (_, i) => `${String(8 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const OCC_D1 = [210, 940, 1820, 2710, 3560, 4280, 4890, 5310, 5482, 5240, 4790, 4380, 4120, 4260, 4310, 4020, 3610, 3050, 2380, 1640, 920, 410, 120];
const OCC_D2 = [180, 820, 1640, 2480, 3290, 3980, 4610, 5106, 4980, 4720, 4310, 3950, 3820, 3990, 4105, 4217];
export const NOW_SLOT = OCC_TIMES[OCC_D2.length - 1];

const addMap = (base, d) => Object.fromEntries(Object.keys(base).map((k) => [k, base[k] + (d[k] || 0)]));
const sumMap = (a, b) => Object.fromEntries(Object.keys(a).map((k) => [k, a[k] + (b[k] || 0)]));

function withDelta(base, d) {
  const out = { ...base };
  ["unique", "inside", "cv", "entries", "exits", "reentries", "cvParticipants"].forEach((k) => { out[k] = base[k] + d[k]; });
  out.ranking = addMap(base.ranking, d.byCompany);
  out.titulaciones = addMap(base.titulaciones, d.byTitulacion);
  return out;
}

export function buildDashboardView(view, state) {
  const d = liveDelta(state);
  const day2 = withDelta(DAY2, d);
  let v;
  if (view === "day1") v = DAY1;
  else if (view === "total") {
    v = {
      unique: 9873 + d.unique, inside: day2.inside, cv: DAY1.cv + day2.cv, entries: DAY1.entries + day2.entries,
      exits: DAY1.exits + day2.exits, reentries: DAY1.reentries + day2.reentries, cvParticipants: 8640 + d.cvParticipants,
      companiesVisited: 3.4, peak: { ...DAY1.peak, label: "Día 1" },
      ranking: sumMap(DAY1.ranking, day2.ranking), titulaciones: sumMap(DAY1.titulaciones, day2.titulaciones),
    };
  } else v = day2;

  const occupancy = OCC_TIMES.map((time, i) => ({
    time,
    d1: OCC_D1[i],
    d2: i < OCC_D2.length ? (i === OCC_D2.length - 1 ? day2.inside : OCC_D2[i]) : null,
  }));
  const isLiveScope = view !== "day1";
  return {
    view,
    isLive: view === "live",
    kpis: { registrados: REGISTERED_BASE + d.registered, unique: v.unique, inside: v.inside, empresas: 128, cv: v.cv },
    deltas: isLiveScope ? { registrados: d.registered, unique: d.unique, inside: d.inside, cv: d.cv, entries: d.entries, exits: d.exits, reentries: d.reentries } : {},
    flows: { entries: v.entries, exits: v.exits, reentries: v.reentries },
    peak: v.peak,
    interaction: { cv: v.cv, cvParticipants: v.cvParticipants, avg: v.cv / v.cvParticipants, companiesVisited: v.companiesVisited },
    ranking: COMPANIES.map((c) => ({ id: c.id, name: c.name, value: v.ranking[c.id], delta: isLiveScope ? d.byCompany[c.id] || 0 : 0 })).sort((a, b) => b.value - a.value),
    centros: Object.entries(addMap(CENTROS_BASE, d.byCentro)).map(([name, value]) => ({ name, value })),
    titulaciones: TITULACION_LIST.map((t) => ({ name: shortTit(t), value: v.titulaciones[t] })).sort((a, b) => b.value - a.value),
    occupancy,
    series: view === "total" ? ["d1", "d2"] : view === "day1" ? ["d1"] : ["d2"],
  };
}

const BOSCH_HOURLY_D1 = [8, 19, 31, 34, 26, 17, 21, 24, 18, 11, 6];
const BOSCH_HOURLY_D2 = [11, 22, 29, 27, 19, 12, 6, 0, 0, 0, 0];
const BOSCH_DEVICES = { "RD-01": 52, "RD-02": 44, "RD-03": 30 };
const BOSCH_TIT = { "Ingeniería Informática": 97, "Ingeniería Industrial": 87, Telecomunicaciones: 52, "Ciencia de Datos": 38, "Ingeniería Eléctrica": 27, "Administración y Dirección de Empresas": 16, "Ingeniería Civil": 13, "Ingeniería Química": 11 };
const BOSCH_CENTROS = { ETSIINF: 135, ETSII: 125, ETSIT: 52, FADE: 16, ETSICCP: 13 };

const hourBucket = (time) => Math.min(19, Math.max(9, Number(time.split(":")[0]))) - 9;

export function companyStats(state, companyId = "bosch") {
  const pMap = Object.fromEntries(state.participants.map((p) => [p.id, p]));
  const fresh = state.interactions.filter((i) => i.companyId === companyId && i.id.startsWith("live-"));
  const delta = fresh.length;
  const devices = { ...BOSCH_DEVICES }, tit = { ...BOSCH_TIT }, centros = { ...BOSCH_CENTROS };
  const hourly = BOSCH_HOURLY_D2.slice();
  fresh.forEach((i) => {
    inc(devices, i.deviceId);
    const p = pMap[i.participantId];
    if (p) { inc(tit, p.titulacion); inc(centros, p.centro); }
    hourly[hourBucket(i.time)]++;
  });
  return {
    total: 341 + delta,
    today: 126 + delta,
    day1: 215,
    day2: 126 + delta,
    delta,
    hourly: BOSCH_HOURLY_D1.map((d1, idx) => ({ hour: `${9 + idx}:00`, dia1: d1, dia2: hourly[idx] })),
    devices: DEVICES.map((dv) => ({ ...dv, value: devices[dv.id] || 0 })),
    titulaciones: Object.entries(tit).map(([name, value]) => ({ name: shortTit(name), value })).sort((a, b) => b.value - a.value),
    centros: Object.entries(centros).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
  };
}
